// Tenant resolution.
//
// Default: tenant = hostname subdomain (`vladdemo.centerbase.ai` → `vladdemo`).
//
// Shared-host mode (spec 033): on a single shared host (VITE_PATH_TENANT_HOST, e.g.
// `reports.centerbase.ai`) there's no per-tenant subdomain, so the user picks the
// site once via a pre-login prompt (Login.jsx). We remember it in localStorage.
// The tenant is only needed BEFORE login (to pick the Auth0 org in the getorgid
// handshake); after login cb-api routes by the JWT `cbsite` claim regardless of URL.

const PATH_TENANT_HOST = import.meta.env.VITE_PATH_TENANT_HOST || ''
const SITE_KEY = 'cb_site'

const defaultTenant = () => import.meta.env.VITE_DEFAULT_TENANT || 'dallasdemo'

// True on the single shared multi-tenant host (not on subdomain hosts / localhost).
export const isSharedHost = () =>
  !!PATH_TENANT_HOST && window.location.hostname === PATH_TENANT_HOST

// The site the user picked on the shared host (localStorage). '' until chosen.
export const getStoredSite = () => {
  try { return localStorage.getItem(SITE_KEY) || '' } catch { return '' }
}
export const setStoredSite = (site) => {
  try { localStorage.setItem(SITE_KEY, site) } catch { /* storage unavailable */ }
}
export const clearStoredSite = () => {
  try { localStorage.removeItem(SITE_KEY) } catch { /* no-op */ }
}

/**
 * The Auth0 handoff marker (CC-53416).
 *
 * 🔴 THE DEAD END THIS EXISTS TO BREAK. A remembered site makes `Login` skip the
 * prompt and redirect to Auth0 immediately, before it renders anything. That is
 * right for the happy path and a trap for every other one: if Auth0 will not let
 * you in — wrong firm, no membership, or you simply typo'd a site name that
 * happens to be a REAL tenant — you land on Auth0's own page, which has no way
 * back to cb2's site field. Returning to the app just runs the same redirect
 * again. Reproduced on deployed QA 2026-09-01: navigating to
 * reports.centerbase.us bounced straight to another firm's Auth0 page with no cb2
 * screen rendered at all, and the only escape was clearing site data by hand.
 *
 * So: stamp the site here immediately BEFORE handing off to Auth0, and clear it
 * once authentication actually succeeds. Arriving back at the login screen,
 * unauthenticated, with the marker still set for the same site means the round
 * trip failed — show the prompt instead of redirecting into it a second time.
 *
 * `sessionStorage`, not `localStorage`, on purpose: this describes one tab's
 * in-flight attempt, not a durable preference. A new tab should get the normal
 * fast path, and closing the browser should not leave a user stuck at the prompt.
 *
 * Note the marker records WHICH site was attempted. Comparing it to the current
 * site means changing sites always gets a clean redirect, and only a genuine
 * repeat of the same failing site is intercepted.
 */
const HANDOFF_KEY = 'cb_site_handoff'

export const markSiteHandoff = (site) => {
  try { sessionStorage.setItem(HANDOFF_KEY, site) } catch { /* storage unavailable */ }
}
export const getSiteHandoff = () => {
  try { return sessionStorage.getItem(HANDOFF_KEY) || '' } catch { return '' }
}
export const clearSiteHandoff = () => {
  try { sessionStorage.removeItem(HANDOFF_KEY) } catch { /* no-op */ }
}

export const getTenant = () => {
  const hostname = window.location.hostname
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return defaultTenant()
  }
  if (isSharedHost()) {
    // Chosen site (prompt/remembered); '' until picked → Login shows the prompt.
    return getStoredSite()
  }
  return hostname.split('.')[0]
}

// Backward-compatible name for the ~5 existing callers.
export const getTenantFromHostname = getTenant

export const getTenantApiHost = () => `${getTenant()}.centerbase.com`
